/**
 * OfflineMediaCapture
 * ───────────────────
 * Handles before/after/odometer photo capture and customer signature
 * collection during job completion. Works fully offline — media is stored
 * in IndexedDB and synced to Supabase Storage on reconnect.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';

// ─── IndexedDB helpers ────────────────────────────────────────────────────────
const MEDIA_DB_NAME = 'serviceJockeyMediaQueue';
const MEDIA_DB_VER  = 1;
const MEDIA_STORE   = 'pendingMedia';

function openMediaDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(MEDIA_DB_NAME, MEDIA_DB_VER);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(MEDIA_STORE)) {
        const store = db.createObjectStore(MEDIA_STORE, { keyPath: 'localId', autoIncrement: true });
        store.createIndex('ticketId', 'ticketId', { unique: false });
        store.createIndex('synced',   'synced',   { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function mediaDbAdd(record) {
  const db = await openMediaDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(MEDIA_STORE, 'readwrite');
    const req = tx.objectStore(MEDIA_STORE).add(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function mediaDbGetByTicket(ticketId) {
  const db = await openMediaDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(MEDIA_STORE, 'readonly');
    const index = tx.objectStore(MEDIA_STORE).index('ticketId');
    const req   = index.getAll(ticketId);
    req.onsuccess = () => resolve(req.result || []);
    req.onerror   = () => reject(req.error);
  });
}

async function mediaDbPut(record) {
  const db = await openMediaDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(MEDIA_STORE, 'readwrite');
    const req = tx.objectStore(MEDIA_STORE).put(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function mediaDbDelete(localId) {
  const db = await openMediaDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(MEDIA_STORE, 'readwrite');
    const req = tx.objectStore(MEDIA_STORE).delete(localId);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

// ─── Export: sync pending media for a ticket ─────────────────────────────────
export async function syncPendingMediaForTicket(ticketId, supabase) {
  try {
    const pending = await mediaDbGetByTicket(ticketId);
    const unsynced = pending?.filter(m => !m?.synced);
    if (!unsynced?.length) return { synced: 0, failed: 0 };

    let synced = 0;
    let failed = 0;

    for (const item of unsynced) {
      try {
        if (item?.mediaType === 'signature') {
          // Upload signature as PNG
          const base64Data = item?.dataUrl?.split(',')?.[1];
          const byteString = atob(base64Data);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString?.length; i++) ia[i] = byteString?.charCodeAt(i);
          const blob = new Blob([ab], { type: 'image/png' });

          const fileName = `signatures/${ticketId}/${item?.fileName}`;
          const { error: uploadErr } = await supabase?.storage
            ?.from('ticket-photos')
            ?.upload(fileName, blob, { upsert: true, contentType: 'image/png' });

          if (!uploadErr) {
            const { data: urlData } = supabase?.storage?.from('ticket-photos')?.getPublicUrl(fileName);
            await supabase?.from('ticket_photos')?.insert({
              ticket_id: ticketId,
              file_name: item?.fileName,
              file_url: urlData?.publicUrl,
              caption: 'Customer Signature',
              photo_type: 'signature',
              organization_id: item?.organizationId || null,
            });
            await mediaDbPut({ ...item, synced: true, syncedAt: new Date()?.toISOString() });
            synced++;
          } else {
            failed++;
          }
        } else {
          // Upload photo (before/after/odometer)
          const mimeType = item?.dataUrl?.split(',')?.[0]?.split(':')?.[1]?.split(';')?.[0] || 'image/jpeg';
          const base64Data = item?.dataUrl?.split(',')?.[1];
          const byteString = atob(base64Data);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString?.length; i++) ia[i] = byteString?.charCodeAt(i);
          const blob = new Blob([ab], { type: mimeType });

          const folder = item?.photoType || 'general';
          const fileName = `${folder}/${ticketId}/${item?.fileName}`;
          const { error: uploadErr } = await supabase?.storage
            ?.from('ticket-photos')
            ?.upload(fileName, blob, { upsert: true, contentType: mimeType });

          if (!uploadErr) {
            const { data: urlData } = supabase?.storage?.from('ticket-photos')?.getPublicUrl(fileName);
            await supabase?.from('ticket_photos')?.insert({
              ticket_id: ticketId,
              file_name: item?.fileName,
              file_url: urlData?.publicUrl,
              caption: item?.caption || item?.photoType || '',
              photo_type: item?.photoType || 'general',
              organization_id: item?.organizationId || null,
            });
            await mediaDbPut({ ...item, synced: true, syncedAt: new Date()?.toISOString() });
            synced++;
          } else {
            failed++;
          }
        }
      } catch (err) {
        console.warn('Media sync failed for item', item?.localId, err);
        failed++;
      }
    }
    return { synced, failed };
  } catch (err) {
    console.warn('syncPendingMediaForTicket error:', err);
    return { synced: 0, failed: 0 };
  }
}

// ─── SignaturePad sub-component ───────────────────────────────────────────────
function SignaturePad({ onSignatureCapture, onClear, existingDataUrl }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPos   = useRef(null);
  const [hasSignature, setHasSignature] = useState(!!existingDataUrl);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx?.fillRect(0, 0, canvas?.width, canvas?.height);
    if (existingDataUrl) {
      const img = new Image();
      img.onload = () => ctx?.drawImage(img, 0, 0);
      img.src = existingDataUrl;
    }
  }, [existingDataUrl]);

  const getPos = (e, canvas) => {
    const rect = canvas?.getBoundingClientRect();
    const scaleX = canvas?.width / rect?.width;
    const scaleY = canvas?.height / rect?.height;
    if (e?.touches) {
      return {
        x: (e?.touches?.[0]?.clientX - rect?.left) * scaleX,
        y: (e?.touches?.[0]?.clientY - rect?.top)  * scaleY,
      };
    }
    return {
      x: (e?.clientX - rect?.left) * scaleX,
      y: (e?.clientY - rect?.top)  * scaleY,
    };
  };

  const startDraw = (e) => {
    e?.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e, canvasRef?.current);
  };

  const draw = (e) => {
    e?.preventDefault();
    if (!drawing?.current) return;
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    const pos = getPos(e, canvas);
    ctx?.beginPath();
    ctx?.moveTo(lastPos?.current?.x, lastPos?.current?.y);
    ctx?.lineTo(pos?.x, pos?.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx?.stroke();
    lastPos.current = pos;
    setHasSignature(true);
  };

  const stopDraw = (e) => {
    e?.preventDefault();
    if (!drawing?.current) return;
    drawing.current = false;
    const dataUrl = canvasRef?.current?.toDataURL('image/png');
    onSignatureCapture?.(dataUrl);
  };

  const handleClear = () => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx?.fillRect(0, 0, canvas?.width, canvas?.height);
    setHasSignature(false);
    onClear?.();
  };

  return (
    <div className="space-y-2">
      <div className="relative border-2 border-dashed border-border rounded-xl overflow-hidden bg-white"
           style={{ touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          width={480}
          height={160}
          className="w-full cursor-crosshair"
          style={{ display: 'block' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-muted-foreground">Sign here</p>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleClear}
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
      >
        <Icon name="RotateCcw" size={12} />
        Clear signature
      </button>
    </div>
  );
}

// ─── PhotoSlot sub-component ──────────────────────────────────────────────────
function PhotoSlot({ label, photoType, dataUrl, onCapture, onRemove }) {
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onCapture?.(ev?.target?.result, file?.name, photoType);
    reader?.readAsDataURL(file);
    e.target.value = '';
  };

  const iconMap = {
    before:   'Camera',
    after:    'CheckSquare',
    odometer: 'Gauge',
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-foreground capitalize">{label}</span>
      {dataUrl ? (
        <div className="relative rounded-xl overflow-hidden border border-border bg-muted aspect-video">
          <img src={dataUrl} alt={label} className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center hover:bg-black/80 transition-colors"
          >
            <Icon name="X" size={12} color="#fff" />
          </button>
          <div className="absolute bottom-1.5 left-1.5 bg-green-600 text-white text-xs px-1.5 py-0.5 rounded-md flex items-center gap-1">
            <Icon name="Check" size={10} color="#fff" />
            Captured
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef?.current?.click()}
          className="aspect-video border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-primary/5 transition-colors bg-muted/30"
        >
          <Icon name={iconMap?.[photoType] || 'Camera'} size={22} color="currentColor" className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Tap to capture</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}

// ─── Main OfflineMediaCapture component ──────────────────────────────────────
const OfflineMediaCapture = ({ ticketId, organizationId, isOnline, onChange, hideSignature = false }) => {
  const [photos, setPhotos] = useState({ before: null, after: null, odometer: null });
  const [photoFileNames, setPhotoFileNames] = useState({ before: null, after: null, odometer: null });
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  // Load any previously saved (unsynced) media for this ticket
  useEffect(() => {
    if (!ticketId) return;
    mediaDbGetByTicket(ticketId)?.then((items) => {
      const unsynced = items?.filter(m => !m?.synced);
      const photoMap = { before: null, after: null, odometer: null };
      const nameMap  = { before: null, after: null, odometer: null };
      let sig = null;
      for (const item of unsynced) {
        if (item?.mediaType === 'signature') {
          sig = item?.dataUrl;
        } else if (item?.photoType && photoMap?.[item?.photoType] !== undefined) {
          photoMap[item.photoType] = item?.dataUrl;
          nameMap[item.photoType]  = item?.fileName;
        }
      }
      setPhotos(photoMap);
      setPhotoFileNames(nameMap);
      if (sig) setSignatureDataUrl(sig);
      setSavedCount(unsynced?.length);
    })?.catch(() => {});
  }, [ticketId]);

  const notifyParent = useCallback((updatedPhotos, updatedSig) => {
    onChange?.({
      photos: updatedPhotos,
      signature: updatedSig,
      hasMedia: Object.values(updatedPhotos)?.some(Boolean) || !!updatedSig,
    });
  }, [onChange]);

  const handlePhotoCapture = useCallback(async (dataUrl, originalFileName, photoType) => {
    const ts = Date.now();
    const ext = originalFileName?.split('.')?.pop() || 'jpg';
    const fileName = `${photoType}_${ticketId}_${ts}.${ext}`;

    const updatedPhotos = { ...photos, [photoType]: dataUrl };
    const updatedNames  = { ...photoFileNames, [photoType]: fileName };
    setPhotos(updatedPhotos);
    setPhotoFileNames(updatedNames);

    setSaving(true);
    try {
      await mediaDbAdd({
        ticketId,
        organizationId,
        mediaType: 'photo',
        photoType,
        dataUrl,
        fileName,
        caption: photoType,
        savedAt: new Date()?.toISOString(),
        synced: false,
      });
      setSavedCount(c => c + 1);
    } catch (err) {
      console.warn('Failed to save photo to IndexedDB:', err);
    } finally {
      setSaving(false);
    }
    notifyParent(updatedPhotos, signatureDataUrl);
  }, [photos, photoFileNames, ticketId, organizationId, signatureDataUrl, notifyParent]);

  const handlePhotoRemove = useCallback((photoType) => {
    const updatedPhotos = { ...photos, [photoType]: null };
    setPhotos(updatedPhotos);
    notifyParent(updatedPhotos, signatureDataUrl);
  }, [photos, signatureDataUrl, notifyParent]);

  const handleSignatureCapture = useCallback(async (dataUrl) => {
    setSignatureDataUrl(dataUrl);
    const fileName = `signature_${ticketId}_${Date.now()}.png`;
    try {
      await mediaDbAdd({
        ticketId,
        organizationId,
        mediaType: 'signature',
        dataUrl,
        fileName,
        savedAt: new Date()?.toISOString(),
        synced: false,
      });
      setSavedCount(c => c + 1);
    } catch (err) {
      console.warn('Failed to save signature to IndexedDB:', err);
    }
    notifyParent(photos, dataUrl);
  }, [ticketId, organizationId, photos, notifyParent]);

  const handleSignatureClear = useCallback(() => {
    setSignatureDataUrl(null);
    notifyParent(photos, null);
  }, [photos, notifyParent]);

  const photoSlots = [
    { key: 'before',   label: 'Before Photo'   },
    { key: 'after',    label: 'After Photo'     },
    { key: 'odometer', label: 'Odometer / Unit' },
  ];

  return (
    <div className="space-y-4">
      {/* Offline indicator */}
      {!isOnline && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
          <Icon name="WifiOff" size={14} color="#d97706" />
          <p className="text-xs text-amber-700 font-medium">
            Offline — photos &amp; signature saved locally and will sync when reconnected.
          </p>
        </div>
      )}
      {isOnline && savedCount > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl">
          <Icon name="CloudUpload" size={14} color="#16a34a" />
          <p className="text-xs text-green-700 font-medium">
            {savedCount} media item{savedCount !== 1 ? 's' : ''} queued — will upload on completion.
          </p>
        </div>
      )}
      {/* Photo capture grid */}
      <div>
        <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5">
          <Icon name="Camera" size={15} color="currentColor" />
          Job Photos
          <span className="text-xs text-muted-foreground font-normal">(optional)</span>
        </p>
        <div className="grid grid-cols-3 gap-2">
          {photoSlots?.map(slot => (
            <PhotoSlot
              key={slot?.key}
              label={slot?.label}
              photoType={slot?.key}
              dataUrl={photos?.[slot?.key]}
              onCapture={handlePhotoCapture}
              onRemove={() => handlePhotoRemove(slot?.key)}
            />
          ))}
        </div>
      </div>
      {/* Signature capture */}
      {!hideSignature && (
      <div>
        <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-1.5">
          <Icon name="PenLine" size={15} color="currentColor" />
          Customer Signature
          <span className="text-xs text-muted-foreground font-normal">(optional)</span>
        </p>
        <SignaturePad
          onSignatureCapture={handleSignatureCapture}
          onClear={handleSignatureClear}
          existingDataUrl={signatureDataUrl}
        />
        {signatureDataUrl && (
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
            <Icon name="CheckCircle" size={11} color="#16a34a" />
            Signature captured
          </p>
        )}
      </div>
      )}
      {saving && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Icon name="Loader" size={11} color="currentColor" className="animate-spin" />
          Saving locally…
        </p>
      )}
    </div>
  );
};

export { mediaDbGetByTicket, mediaDbPut, mediaDbDelete };
export default OfflineMediaCapture;
